/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable camelcase */
import styles from 'assets/jss/material-dashboard-pro-react/components/addressAutocompleteStyle';
import React from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { toast } from 'sonner';
import usePlacesAutocomplete from 'use-places-autocomplete';

import ButtonBase from '@material-ui/core/ButtonBase';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';

import CustomInput from 'creative-components/CustomInput/CustomInput';
import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';

import { handlePlacesAutocompleteSuggestionSelect } from 'utils/places';

const useStyles = makeStyles(styles);

// Should be used as the input component for the main address field,
// but also requires fields for address2, city, state, zip, and location.
//
// Uses valueName for the prefix for all of the field names in the form.
// E.g. companyAddress1, companyAddress2, etc...
const AddressAutocomplete = ({
  valueName,
  inputProps,
  setFieldValue,
  initialValue,
  disabled = false,
  showClearButton = false,
  ...props
}) => {
  const classes = useStyles();
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      /* Define search scope here */
      // types: ['address'],
      types: ['geocode'],
      componentRestrictions: {
        country: 'us',
      },
    },
  });

  React.useEffect(() => {
    if (initialValue) {
      // When user selects a place, we can replace the keyword without request data from API
      // by setting the second parameter to "false"
      setValue(initialValue, false);
    }
  }, [initialValue]);

  const ref = useOnclickOutside(() => {
    // When user clicks outside of the component, we can dismiss
    // the searched suggestions by calling this method
    clearSuggestions();
  });

  const handleInput = (e) => {
    // Update the keyword of the input element
    setValue(e.target.value);
  };

  const handleSelect = async (suggestion) => {
    const address = await handlePlacesAutocompleteSuggestionSelect(suggestion);

    if (!address) {
      toast.error('We were unable to look up that location. Please try again.');
      return;
    }

    // When user selects a place, we can replace the keyword without request data from API
    // by setting the second parameter to "false"
    setValue(address.address1, false);
    clearSuggestions();

    // NOTE: These field names MUST be exact in the form inputs!
    setFieldValue(`${valueName}.address1`, address.address1, true);
    setFieldValue(`${valueName}.address2`, address.address2, true);
    setFieldValue(`${valueName}.city`, address.city, true);
    setFieldValue(`${valueName}.state`, address.state, true);
    setFieldValue(`${valueName}.zipcode`, address.zipcode, true);
    setFieldValue(`${valueName}.location`, address.location, true);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();

    clearSuggestions();
    setValue('', false);

    setFieldValue(`${valueName}.address1`, '', true);
    setFieldValue(`${valueName}.address2`, '', true);
    setFieldValue(`${valueName}.city`, '', true);
    setFieldValue(`${valueName}.state`, '', true);
    setFieldValue(`${valueName}.zipcode`, '', true);
    setFieldValue(`${valueName}.location`, {}, true);
  };

  const renderSuggestions = () => (
    <List>
      {data.map((suggestion) => {
        const {
          id,
          structured_formatting: { main_text, secondary_text },
        } = suggestion;

        return (
          <ButtonBase
            key={`${main_text}${secondary_text}`}
            disableRipple
            disableTouchRipple
            onClick={() => handleSelect(suggestion)}
          >
            <ListItem button dense>
              <ListItemText primary={main_text} secondary={secondary_text} />
            </ListItem>
          </ButtonBase>
        );
      })}
    </List>
  );

  // TODO: Just use dashboard one everywhere once WP site is live?
  // Two different styled inputs depending on main page vs dashboard
  let InputComponent = CustomInput;
  if (window.location.pathname.startsWith('/dashboard')) InputComponent = CustomInputDashboard;

  const shouldShowClearButton = showClearButton && Boolean(value) && !disabled;

  const inputPropsWithControls = {
    ...inputProps,
    value,
    disabled: !ready || disabled,
    onChange: handleInput,
  };

  if (shouldShowClearButton) {
    const existingEndAdornment = inputProps && inputProps.endAdornment;
    inputPropsWithControls.endAdornment = (
      <>
        {existingEndAdornment}
        <ButtonBase
          disableRipple
          disableTouchRipple
          onClick={handleClear}
          style={{ padding: '4px' }}
        >
          <ClearIcon fontSize="small" />
        </ButtonBase>
      </>
    );
  }

  return (
    <span ref={ref}>
      {window.location.pathname.startsWith('/dashboard')}
      <InputComponent
        {...props}
        inputProps={{
          ...inputPropsWithControls,
        }}
      />
      {/* We can use the "status" to decide whether we should display the dropdown or not */}
      {status === 'OK' && renderSuggestions()}
    </span>
  );
};

export default AddressAutocomplete;
